/**
 * Code Snippets for Interactive Playground
 * Highlights the comprehensive capabilities of Draway Auth
 */

export const CODE_SNIPPETS: Record<string, string> = {
  react: `<span class="token-comment">// 1. Drop-in Provider with Custom Domain</span>
<span class="token-kw">import</span> { DrawayAuthProvider, useDrawayAuth } <span class="token-kw">from</span> <span class="token-str">'@draway/auth-react'</span>;

<span class="token-kw">export default function</span> <span class="token-fn">App</span>() {
  <span class="token-kw">return</span> (
    &lt;<span class="token-fn">DrawayAuthProvider</span> 
      <span class="token-str">apiKey</span>=<span class="token-str">"ak_live_79a2f..."</span> 
      <span class="token-str">domain</span>=<span class="token-str">"auth.yourdomain.com"</span>&gt;
      &lt;<span class="token-fn">EnterpriseDashboard</span> /&gt;
    &lt;/<span class="token-fn">DrawayAuthProvider</span>&gt;
  );
}

<span class="token-comment">// 2. Passwordless Biometric Sign-in or WhatsApp OTP</span>
<span class="token-kw">function</span> <span class="token-fn">AuthActions</span>() {
  <span class="token-kw">const</span> { loginWithPasskey, requestOtp } = <span class="token-fn">useDrawayAuth</span>();

  <span class="token-kw">return</span> (
    &lt;<span class="token-fn">div</span>&gt;
      &lt;<span class="token-fn">button</span> <span class="token-str">onClick</span>={() => <span class="token-fn">loginWithPasskey</span>()}&gt;
        Biometric Sign In (TouchID / FaceID)
      &lt;/<span class="token-fn">button</span>&gt;

      &lt;<span class="token-fn">button</span> <span class="token-str">onClick</span>={() => <span class="token-fn">requestOtp</span>(<span class="token-str">'+14155552671'</span>, <span class="token-str">'whatsapp'</span>)}&gt;
        Send WhatsApp Login Code
      &lt;/<span class="token-fn">button</span>&gt;
    &lt;/<span class="token-fn">div</span>&gt;
  );
}`,

  node: `<span class="token-comment">// Node.js / Express Server-Side Middleware</span>
<span class="token-kw">import</span> express <span class="token-kw">from</span> <span class="token-str">'express'</span>;
<span class="token-kw">import</span> { drawayAuthMiddleware } <span class="token-kw">from</span> <span class="token-str">'@draway/auth-node'</span>;

<span class="token-kw">const</span> app = <span class="token-fn">express</span>();

<span class="token-comment">// Hardened token verification at edge/server with zero latency</span>
app.<span class="token-fn">use</span>(<span class="token-fn">drawayAuthMiddleware</span>({
  apiKey: process.env.<span class="token-str">DRAWAY_API_KEY</span>,
  requireMfaVerified: <span class="token-kw">true</span>,        <span class="token-comment">// Rejects pre-auth mfaTokens</span>
  enforceTenantIsolation: <span class="token-kw">true</span>     <span class="token-comment">// Resolves tenant by x-api-key or Host</span>
}));

app.<span class="token-fn">get</span>(<span class="token-str">'/api/org/data'</span>, (req, res) => {
  res.<span class="token-fn">json</span>({ 
    tenant: req.client.name, 
    userId: req.user.id, 
    mfaMethod: req.user.preferredMfaMethod, <span class="token-comment">// 'passkey' | 'whatsapp' | 'totp' | 'sms'</span>
    customDomain: req.client.customDomain 
  });
});`,

  ts: `<span class="token-comment">// Vanilla TypeScript SDK - Zero UI Lock-in</span>
<span class="token-kw">import</span> { DrawayAuth } <span class="token-kw">from</span> <span class="token-str">'@draway/auth-sdk'</span>;

<span class="token-kw">const</span> auth = <span class="token-kw">new</span> <span class="token-fn">DrawayAuth</span>(<span class="token-str">'ak_live_79a2f...'</span>, <span class="token-str">'https://auth.yourdomain.com'</span>);

<span class="token-comment">// 1. Primary auth returning partial session</span>
<span class="token-kw">const</span> res = <span class="token-kw">await</span> auth.<span class="token-fn">login</span>(<span class="token-str">'user@org.com'</span>, <span class="token-str">'secret123'</span>);

<span class="token-kw">if</span> (res.mfaRequired) {
  <span class="token-comment">// 2. Flexible second factor proof: 'totp' | 'whatsapp' | 'email' | 'recovery'</span>
  <span class="token-kw">const</span> session = <span class="token-kw">await</span> auth.<span class="token-fn">verifyLogin</span>(
    <span class="token-str">'user@org.com'</span>, 
    <span class="token-str">'582049'</span>, 
    res.mfaToken, 
    res.preferredMethod || <span class="token-str">'whatsapp'</span>
  );
  console.<span class="token-fn">log</span>(<span class="token-str">'Authenticated!'</span>, session.accessToken);
}`,

  curl: `<span class="token-comment"># 1. Primary Auth (Returns signed mfaToken)</span>
curl -X POST https://api.draway.in/v1/auth/login \\
  -H <span class="token-str">"Content-Type: application/json"</span> \\
  -H <span class="token-str">"x-api-key: ak_live_79a2f..."</span> \\
  -d <span class="token-str">'{"email":"dev@acme.com","password":"••••••••"}'</span>

<span class="token-comment"># 2. Complete MFA Verification (WhatsApp, TOTP, SMS, or Recovery)</span>
curl -X POST https://api.draway.in/v1/auth/verify-login \\
  -H <span class="token-str">"Content-Type: application/json"</span> \\
  -H <span class="token-str">"x-api-key: ak_live_79a2f..."</span> \\
  -H <span class="token-str">"x-mfa-token: mfa_tok_9x28fa..."</span> \\
  -d <span class="token-str">'{"email":"dev@acme.com","code":"582049","method":"whatsapp"}'</span>

<span class="token-comment"># 3. Verify Custom Tenant Domain DNS</span>
curl -X POST https://api.draway.in/v1/clients/verify-domain \\
  -H <span class="token-str">"x-api-key: ak_live_79a2f..."</span>`
};
